import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Package, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/common/Navbar";
import toast from "react-hot-toast";

// ORDER SUCCESS PAGE
export function OrderSuccessPage() {
  const { state } = useLocation();
  const order = state?.order;
  const restaurantName = state?.restaurantName;

  return (
    <>
      <Helmet><title>Order Placed — Khana Spot</title></Helmet>
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-16 text-center page-enter">
        <div className="text-6xl mb-4 animate-bounce">🎉</div>
        <div className="flex justify-center mb-4">
          <CheckCircle className="w-16 h-16 text-green-500" />
        </div>
        <h1 className="font-display text-3xl font-bold text-gray-900">Order Placed!</h1>
        <p className="text-gray-500 mt-2">
          Your order from <strong>{restaurantName}</strong> has been received. The restaurant will confirm soon.
        </p>
        {order && (
          <div className="bg-orange-50 rounded-2xl p-5 mt-6 text-left space-y-2">
            <p className="text-sm"><span className="font-semibold text-gray-700">Order ID:</span> <span className="text-gray-500 font-mono text-xs">{order.id.slice(0, 8).toUpperCase()}</span></p>
            <p className="text-sm"><span className="font-semibold text-gray-700">Name:</span> <span className="text-gray-500">{order.customer_name}</span></p>
            <p className="text-sm"><span className="font-semibold text-gray-700">Phone:</span> <span className="text-gray-500">{order.customer_phone}</span></p>
            <p className="text-sm"><span className="font-semibold text-gray-700">Address:</span> <span className="text-gray-500">{order.customer_address}</span></p>
            <p className="text-sm"><span className="font-semibold text-gray-700">Total:</span> <span className="text-orange-600 font-bold">Rs {order.total?.toLocaleString()}</span></p>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-3 mt-8">
          <Link to="/my-orders" className="flex-1 btn-primary flex items-center justify-center gap-2">
            <Package className="w-4 h-4" /> Track Order
          </Link>
          <Link to="/restaurants" className="flex-1 btn-secondary flex items-center justify-center gap-2">
            Order More
          </Link>
        </div>
      </div>
    </>
  );
}

// LOGIN PAGE
export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { profile: userProfile } = await signIn(form);
      toast.success("Welcome back!");
      if (userProfile?.role === "admin") navigate("/admin");
      else if (userProfile?.role === "restaurant") navigate("/dashboard");
      else navigate(from);
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Sign In — Khana Spot</title></Helmet>
      <Navbar />
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="text-5xl">🍽️</span>
            <h1 className="font-display text-3xl font-bold text-gray-900 mt-3">Welcome Back</h1>
            <p className="text-gray-500 mt-1">Sign in to your account</p>
          </div>
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} required placeholder="    you@example.com" className="input-field pl-10" />
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} required placeholder="    ••••••••" className="input-field pl-10 pr-10" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 mt-2">
                {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Signing in...</> : "Sign In"}
              </button>
            </form>
            <p className="text-center text-sm text-gray-500 mt-6">
              Don't have an account?{" "}
              <Link to="/register" className="text-orange-500 font-semibold hover:text-orange-600">Sign Up</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

// REGISTER PAGE
export function RegisterPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "user" });
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await signUp(form);
      toast.success("Account created! Please check your email to confirm.");
      navigate("/login");
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Create Account — Khana Spot</title></Helmet>
      <Navbar />
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="text-5xl">🍽️</span>
            <h1 className="font-display text-3xl font-bold text-gray-900 mt-3">Create Account</h1>
            <p className="text-gray-500 mt-1">Join Khana Spot today</p>
          </div>
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">Full Name</label>
                <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Muhammad Ali" className="input-field" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} required placeholder="you@example.com" className="input-field" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">Phone Number</label>
                <input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="03XX-XXXXXXX" className="input-field" />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">Password</label>
                <div className="relative">
                  <input type={showPw ? "text" : "password"} value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} required placeholder="Min 6 characters" className="input-field pr-10" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1.5">I am a...</label>
                <div className="grid grid-cols-2 gap-3">
                  {[{ value: "user", label: "👤 Customer" }, { value: "restaurant", label: "🍽️ Restaurant" }].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setForm(f => ({ ...f, role: opt.value }))}
                      className={`py-3 rounded-xl border-2 text-sm font-semibold transition-all ${form.role === opt.value ? "border-orange-500 bg-orange-50 text-orange-600" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 mt-2">
                {loading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating...</> : "Create Account"}
              </button>
            </form>
            <p className="text-center text-sm text-gray-500 mt-6">
              Already have an account?{" "}
              <Link to="/login" className="text-orange-500 font-semibold hover:text-orange-600">Sign In</Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

// NOT FOUND PAGE
export function NotFoundPage() {
  return (
    <>
      <Navbar />
      <div className="min-h-[70vh] flex items-center justify-center text-center px-4">
        <div>
          <div className="text-8xl mb-4">🍽️</div>
          <h1 className="font-display text-4xl font-bold text-gray-900">404</h1>
          <p className="text-gray-500 mt-2 mb-6">Oops! This page doesn't exist.</p>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    </>
  );
}

export default OrderSuccessPage;
